import { useEffect, useState } from 'react';

/**
 * THE PORTAL NOTICES ITS OWN NEW VERSIONS.
 *
 * Every deploy renames the main JS bundle (content hash), so comparing the
 * bundle name this tab booted with against the one index.html serves NOW is
 * an exact "there is a newer portal" test — no server endpoint, no version
 * table, nothing to maintain.
 *
 * What happens on a mismatch is deliberate:
 *  · tab in the BACKGROUND → reload silently. Nobody is typing; when they
 *    come back, they are simply on the new version.
 *  · tab in FRONT → never yank the page (an unsaved note would die with it).
 *    The hook returns true and the app shows a small "update" banner.
 *
 * Checks: the moment the tab regains focus — which is exactly when people
 * used to be told "refresh to see the changes" — plus a 30-minute safety net
 * for the tab that stays open (and in front) all day. No tighter loop: with
 * a deploy every few days, anything more is requests for nothing.
 */
export function useNewVersion(): boolean {
  const [fresh, setFresh] = useState(false);
  useEffect(() => {
    const current = (document.querySelector('script[type="module"][src*="/assets/"]') as HTMLScriptElement | null)
      ?.src.match(/assets\/(index-[\w-]+)\.js/)?.[1] || '';
    if (!current) return;
    let stop = false;
    const check = async () => {
      try {
        const r = await fetch(`/index.html?v=${Date.now()}`, { cache: 'no-store' });
        if (!r.ok) return;
        const m = (await r.text()).match(/assets\/(index-[\w-]+)\.js/);
        if (stop || !m || m[1] === current) return;
        if (document.hidden) window.location.reload();
        else setFresh(true);
      } catch { /* offline or mid-deploy — the next check will say */ }
    };
    const iv = setInterval(() => { void check(); }, 30 * 60_000);
    const onWake = () => { if (!document.hidden) void check(); };
    document.addEventListener('visibilitychange', onWake);
    window.addEventListener('focus', onWake);
    return () => {
      stop = true;
      clearInterval(iv);
      document.removeEventListener('visibilitychange', onWake);
      window.removeEventListener('focus', onWake);
    };
  }, []);
  return fresh;
}
